import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';
import { fetchShopOnlineBalanceLedger } from '../services/api';
import type { ShopUsdWalletLedgerResponse } from '../types';

export type UseShopOnlineBalanceLedgerParams = {
  currency?: string;
  type?: string;
  page?: number;
  limit?: number;
  sortBy?: string | null;
  sortOrder?: SortDirection | null;
  enabled?: boolean;
};

export function useShopOnlineBalanceLedger(params: UseShopOnlineBalanceLedgerParams = {}) {
  const {
    currency = 'USD',
    type,
    page = 1,
    limit = 50,
    sortBy,
    sortOrder,
    enabled = true,
  } = params;

  const [data, setData] = useState<ShopUsdWalletLedgerResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchShopOnlineBalanceLedger({
        currency,
        ...(type ? { type } : {}),
        page,
        limit,
        ...(sortBy ? { sortBy } : {}),
        ...(sortOrder ? { sortOrder } : {}),
      });
      if (res.success && res.data) setData(res.data);
      else setData(null);
    } catch {
      toast.error('Could not load online balance ledger');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [enabled, currency, type, page, limit, sortBy, sortOrder]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, reload: load };
}
